import { createSlice, createAsyncThunk, PayloadAction, ActionReducerMapBuilder, createSelector } from '@reduxjs/toolkit';
import { createGenericSlice } from "../GenericSlice";
import type { UserStateV2, ServiceProviderRole, PaymentProfile, BusinessProfile } from "../../app/Subscriptions/types/UserStateV2";
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { doc, getDoc, setDoc, updateDoc, Timestamp, DocumentReference, DocumentData } from 'firebase/firestore';
import { auth, db, storage } from '../../Config/firebase';
import type { BankAccountType } from "../../app/Subscriptions/types/userTypes.ts";
import { getAuth } from 'firebase/auth';
import type { RootState } from '../store';
import type { GenericActions } from "../GenericSlice.ts";
import { WritableDraft } from 'immer/dist/internal';
import { PageName } from '../../enums.ts';

// Define RootState type
export interface RootState {
    userSliceV2: State;
}

interface State {
    data: UserStateV2 | null;
    error: string | null;
    status: 'not_loaded' | 'loading' | 'finished' | 'error';
}

const initialState: State = {
    data: null,
    error: null,
    status: 'not_loaded'
};

interface FirestoreUserData extends Omit<UserStateV2, 'administeredPaymentProfiles'> {
    administeredPaymentProfiles?: PaymentProfile[];
}

export const convertFirestoreTypes = (data: DocumentData | null): UserStateV2 => {
    const defaultState: UserStateV2 = {
        myRoles: [],
        uid: '',
        email: '',
        display_name: '',
        photo_url: '',
        activeRole: null,
        administeredPaymentProfiles: [],
        addDummySubscription: false,
        businessProfile: null,
        notifyOnWhatsApp: false,
        phone_number: '',
        userId: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        Credits: 0,
        delegatedRoles: [],
        ServiceTypes: [],
        plundrDescription: '',
        plundrImages: [],
        GSTIN: '',
        mmAlign2monthBoundary: false,
        unmaskCustomerNumbers: false,
        multiMonthPlanToDaysTable: {
            FlexibleMonthly: 30,
            Quarterly: 90,
            Semester: 180,
            Annual: 365
        },
        isAuthenticated: false,
        loading: false,
        error: null,
        paymentProfiles: [],            
        pauseBySubscriber: false,
        allowRoleSwitchToOwner: false,
        customerKYCFields: [],
        meetPlusEnabled: false,
        chequeredEnabled: false,
        durationLimitForMultiMonthSubscriptions: 15,
        countInvoiceThreshold: 3
    };

    if (!data) {
        return defaultState;
    }

    // Convert any Date objects to ISO strings
    const convertDates = (obj: any, seen = new WeakSet()): any => {
        // Handle null or undefined
        if (!obj || typeof obj !== 'object') return obj;

        // Guard against Window, DOM nodes, and other non-serializable objects
        if (typeof window !== 'undefined') {
            if (obj === window || (obj.constructor && obj.constructor.name === 'Window')) return null;
            if (obj instanceof Node) return null;
            if (obj instanceof Event) return null;
        }

        // Handle circular references
        if (seen.has(obj)) return obj;
        seen.add(obj);

        // Handle arrays
        if (Array.isArray(obj)) {
            return obj.map(item => convertDates(item, seen));
        }

        // Handle Date and Timestamp objects
        if (obj instanceof Date) {
            return obj.toISOString();
        }
        if (obj instanceof Timestamp) {
            return obj.toDate().toISOString();
        }

        // Handle plain objects
        const converted = { ...obj };
        for (const key in converted) {
            if (Object.prototype.hasOwnProperty.call(converted, key)) {
                converted[key] = convertDates(converted[key], seen);
            }
        }

        // Handle planDetails timestamps if they exist
        if (converted.planDetails && typeof converted.planDetails === 'object') {
            if (converted.planDetails.expiryDate instanceof Timestamp) {
                converted.planDetails.expiryDate = converted.planDetails.expiryDate.toDate().toISOString();
            }
            if (converted.planDetails.startDate instanceof Timestamp) {
                converted.planDetails.startDate = converted.planDetails.startDate.toDate().toISOString();
            }
        }
        return converted;
    };

    const processedData = convertDates(data);

    return {
        ...defaultState,
        ...processedData,
        myRoles: processedData.myRoles || [],
        activeRole: processedData.activeRole || null,
        administeredPaymentProfiles: processedData.administeredPaymentProfiles || [],
        businessProfile: processedData.businessProfile || null,
        addDummySubscription: processedData.addDummySubscription || false,
        createdAt: processedData.createdAt || defaultState.createdAt,
        updatedAt: processedData.updatedAt || defaultState.updatedAt,
    };
};

export const checkUserInFirestore = createAsyncThunk(
    'UserSliceV2/checkUserInFirestore',
    async (uid: string, { dispatch }) => {  
        try {
           
            const userRef = doc(db, 'ServiceProviders', uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
               
                const rawData = userSnap.data();
               

                // Create base user data with required fields and ensure roles are serializable
                const baseUserData: Partial<UserStateV2> = {
                    ...rawData,
                    uid: uid,
                    display_name: rawData.display_name || '',
                    phone_number: rawData.phone_number || '',
                    email: rawData.email || '',
                    photo_url: rawData.photo_url || '',
                    businessProfile: rawData.businessProfile || {},
                    // Ensure roles are serializable by converting references to strings
                    myRoles: (rawData.myRoles || []).map((role: any) => ({
                        businessName: role.businessName || '',
                        name: role.name || '',
                        number: role.number || '',
                        role: role.role || '',
                        serviceProviderReference: typeof role.serviceProviderReference === 'string'
                            ? role.serviceProviderReference
                            : role.serviceProviderReference?.path || '',
                        userPhoto: role.userPhoto || '',
                        assignedSubscriptions: role.assignedSubscriptions?.map((sub: any) => (sub.id)) || [],
                        [PageName.Inflow]: role.Inflow || false,
                        [PageName.ExportHistory]: role.ExportHistory || false,
                        [PageName.Events]: role.Events || false,
                    })),
                    isAuthenticated: true,
                    loading: false,
                    paymentProfiles: rawData.paymentProfiles || null,
                    administeredPaymentProfiles: null,
                    delegatedRoles : [],
                    allowRoleSwitchToOwner: rawData.allowRoleSwitchToOwner  || false,
                    customerKYCFields: rawData.customerKYCFields || [],
                    durationLimitForMultiMonthSubscriptions : rawData.durationLimitForMultiMonthSubscriptions || 15,
                    countInvoiceThreshold : rawData.countInvoiceThreshold || 3
                };

                // Determine effective service provider ID (for fetching chequeredEnabled and administered payment profiles)
                let effectiveServiceProviderId = uid; // Default to user's own ID
                
                // If there's an active admin role, fetch the administered payment profiles and chequeredEnabled from that service provider
                if (rawData.activeRole?.role && rawData.activeRole?.role !== 'Observer' && rawData.activeRole?.serviceProviderReference) {
                    try {
                        const serviceProviderRef = typeof rawData.activeRole.serviceProviderReference === 'string'
                            ? rawData.activeRole.serviceProviderReference
                            : rawData.activeRole.serviceProviderReference.path || '';

                        // Extract service provider ID from reference path
                        const segments = serviceProviderRef.split('/');
                        effectiveServiceProviderId = segments[segments.length - 1];

                        // Ensure activeRole is serializable
                        baseUserData.activeRole = {
                            ...rawData.activeRole,
                            serviceProviderReference: serviceProviderRef
                        };

                        const adminRef = doc(db, serviceProviderRef);
                        const adminSnap = await getDoc(adminRef);
                        if (adminSnap.exists()) {
                            const adminData = adminSnap.data();
                            baseUserData.administeredPaymentProfiles = adminData.paymentProfiles || null;
                            // Fetch chequeredEnabled from the active service provider
                            baseUserData.chequeredEnabled = adminData.chequeredEnabled === true;
                        }


                    } catch (error) {
                        console.error("Error fetching administered payment profiles:", error);
                        baseUserData.administeredPaymentProfiles = null;
                    }
                } else {
                    // No active role, fetch chequeredEnabled from user's own document
                    baseUserData.chequeredEnabled = rawData.chequeredEnabled === true;
                }

                // If we didn't set chequeredEnabled above (e.g., active role but no admin data), fetch it from effective service provider
                if (baseUserData.chequeredEnabled === undefined) {
                    try {
                        const effectiveRef = doc(db, 'ServiceProviders', effectiveServiceProviderId);
                        const effectiveSnap = await getDoc(effectiveRef);
                        if (effectiveSnap.exists()) {
                            const effectiveData = effectiveSnap.data();
                            baseUserData.chequeredEnabled = effectiveData.chequeredEnabled === true;
                        } else {
                            baseUserData.chequeredEnabled = false;
                        }
                    } catch (error) {
                        console.error("Error fetching chequeredEnabled:", error);
                        baseUserData.chequeredEnabled = false;
                    }
                }

                // Convert non-serializable data
                const convertedData = convertFirestoreTypes(baseUserData);
                console.log("came here -1.6", convertedData)
                return convertedData;
            } else {
                console.log("No user document found for:", uid);
                console.log("came here -1.7")
                return null;
            }


        } catch (error) {
            console.error("Error fetching user data:", error);
            throw error;
        }
    }
);

export const uploadProfilePicture = createAsyncThunk(
    'UserSliceV2/uploadProfilePicture',

    async (file: File, { getState }) => {

        const state = getState() as { userSliceV2: State };
        const userId = state.userSliceV2.data?.uid;       
        if (!userId) throw new Error('No user ID found');

        const timestamp = Date.now().toString();
        const storageRef = ref(storage, `users/${userId}/uploadsref/${timestamp}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
    }
);

// Memoized selectors
const normalizePaymentProfiles = (profiles: PaymentProfile[] | Record<string, PaymentProfile> | null): PaymentProfile[] => {
    if (!profiles) return [];

    // If it's already an array, just return it
    if (Array.isArray(profiles)) {
        return profiles;
    }

    // If it's an object, convert to array
    return Object.values(profiles);
};

export const selectBankAccounts = createSelector(
    [(state: RootState) => state.userSliceV2.data],
    (userData): PaymentProfile[] => {
        if (!userData) return [];

        // If user has a role, ONLY return administered payment profiles
        if (userData.activeRole?.role) {
            return normalizePaymentProfiles(userData.administeredPaymentProfiles);
        }

        // For Owner, return user's own payment profiles
        return normalizePaymentProfiles(userData.paymentProfiles);
    }
);

export const selectActiveRole = createSelector(
    [(state: RootState) => state.userSliceV2.data],
    (userData) => userData?.activeRole ?? null
);

export const selectAvailableRoles = createSelector(
    [(state: RootState) => state.userSliceV2.data],
    (userData) => userData?.myRoles ?? []
);

export const selectOriginalUID = createSelector(
    [(state: RootState) => state.userSliceV2.data],
    (userData) => userData?.uid ?? ''
);

export const selectOriginalUserProfile = createSelector(
    [(state: RootState) => state.userSliceV2.data],
    (userData) => {
        if (!userData) return null;

        return {
            uid: userData.uid,
            display_name: userData.display_name,
            phone_number: userData.phone_number,
            email: userData.email,
            photo_url: userData.photo_url,
            businessProfile: userData.businessProfile,
            myRoles: userData.myRoles,
            activeRole: userData.activeRole,
            Credits: userData.Credits,
            ServiceTypes: userData.ServiceTypes,
        };
    }
);

export const selectEffectiveUID = createSelector(
    [(state: RootState) => state.userSliceV2.data],
    (userData) => {
        if (!userData) return '';

        if (userData.activeRole?.serviceProviderReference) {
            const segments = userData.activeRole.serviceProviderReference.split('/');
            return segments[segments.length - 1];
        }

        return userData.uid;
    }
);


export const selectChequeredEnabled = createSelector(
    [(state: RootState) => state.userSliceV2.data],
    (userData) => {
        return userData?.chequeredEnabled === true;
    }
);

export const persistActiveRole = createAsyncThunk(
    'userSliceV2/persistActiveRole',
    async (role: ServiceProviderRole | null, { getState }) => {
        const state = getState() as RootState;
        const user = state.userSliceV2.data;

        if (!user) {
            throw new Error('No authenticated user found');
        }

        try {
            const userRef = doc(db, 'ServiceProviders', user.uid);

            // If role is null, we're clearing the active role
            if (!role) {
               
                await updateDoc(userRef, {
                    activeRole: null
                });
                return {
                    role: null,
                    administeredPaymentProfiles: null
                };
            }

            // Ensure the role object is serializable
            const serializedRole = {
                businessName: role.businessName || '',
                name: role.name || '',
                number: role.number || '',
                role: role.role || '',
                serviceProviderReference: typeof role.serviceProviderReference === 'string'
                    ? role.serviceProviderReference
                    : '',
                userPhoto: role.userPhoto || '',
                assignedSubscriptions: role.assignedSubscriptions || [],
                [PageName.Inflow]: role.Inflow || false,
                [PageName.ExportHistory]: role.ExportHistory || false,
                [PageName.Events]: role.Events || false, 
            };

            

            // Determine effective service provider ID
            let effectiveServiceProviderId = user.uid;
            let administeredPaymentProfiles = null;
            let chequeredEnabled = false;

            // If switching to admin role, fetch the administered payment profiles and chequeredEnabled
            if (role.role && role.serviceProviderReference) {
                try {
                    // Handle serviceProviderReference - it could be a string path or DocumentReference
                    const serviceProviderRef = typeof role.serviceProviderReference === 'string'
                        ? role.serviceProviderReference
                        : role.serviceProviderReference.path || '';
                    
                    const segments = serviceProviderRef.split('/');
                    effectiveServiceProviderId = segments[segments.length - 1];
                    
                    const adminRef = doc(db, serviceProviderRef);
                    const adminSnap = await getDoc(adminRef);
                    if (adminSnap.exists()) {
                        const adminData = adminSnap.data();
                        administeredPaymentProfiles = adminData.paymentProfiles || null;
                        chequeredEnabled = adminData.chequeredEnabled === true;
                    }
                } catch (error) {
                    console.error("Error fetching administered payment profiles:", error);
                }
            } else {
                // No active role, fetch chequeredEnabled from user's own document
                try {
                    const userSnap = await getDoc(userRef);
                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        chequeredEnabled = userData.chequeredEnabled === true;
                    }
                } catch (error) {
                    console.error("Error fetching chequeredEnabled:", error);
                }
            }

            // If we still don't have chequeredEnabled, fetch from effective service provider
            if (chequeredEnabled === false && effectiveServiceProviderId) {
                try {
                    const effectiveRef = doc(db, 'ServiceProviders', effectiveServiceProviderId);
                    const effectiveSnap = await getDoc(effectiveRef);
                    if (effectiveSnap.exists()) {
                        const effectiveData = effectiveSnap.data();
                        chequeredEnabled = effectiveData.chequeredEnabled === true;
                    }
                } catch (error) {
                    console.error("Error fetching chequeredEnabled from effective service provider:", error);
                }
            }

            // Update both the active role and administered payment profiles
            await updateDoc(userRef, {
                activeRole: serializedRole
            });

            return {
                role: serializedRole,
                administeredPaymentProfiles,
                chequeredEnabled
            };
        } catch (error) {
            console.error("Error persisting active role:", error);
            throw error;
        }
    }
);

export const fetchAdministeredPaymentProfiles = createAsyncThunk(
    'UserSliceV2/fetchAdministeredPaymentProfiles',
    async (serviceProviderReference: string) => {
        try {
           
            const adminRef = doc(db, serviceProviderReference);
            const adminSnap = await getDoc(adminRef);

            if (adminSnap.exists()) {
                const adminData = adminSnap.data();
         
                return adminData.paymentProfiles || null;
            }
            return null;
        } catch (error) {
            console.error("Error fetching administered payment profiles:", error);
            throw error;
        }
    }
);

export const fetchChequeredEnabled = createAsyncThunk(
    'UserSliceV2/fetchChequeredEnabled',
    async (serviceProviderId: string) => {
        try {
            const serviceProviderRef = doc(db, 'ServiceProviders', serviceProviderId);
            const serviceProviderSnap = await getDoc(serviceProviderRef);
            
            if (serviceProviderSnap.exists()) {
                const data = serviceProviderSnap.data();
                const enabled = data.chequeredEnabled === true;
                console.log('♟️ Chequered enabled status:', enabled, 'for service provider:', serviceProviderId);
                return enabled;
            }
            return false;
        } catch (error) {
            console.error('Error fetching chequeredEnabled:', error);
            return false;
        }
    }
);

export const updateAdministeredPaymentProfiles = createAsyncThunk(
    'userSliceV2/updateAdministeredPaymentProfiles',
    async (profile: PaymentProfile) => {
        // Implementation here
        return profile;
    }
);

export const removeAdministeredPaymentProfile = createAsyncThunk(
    'userSliceV2/removeAdministeredPaymentProfile',
    async (profile: PaymentProfile) => {
        // Implementation here
        return profile;
    }
);

const UserSliceV2 = createGenericSlice({
    name: "UserSliceV2",
    initialState,
    reducers: {
        success: {
            reducer: (state, action: PayloadAction<UserStateV2>) => {
                // Log specific properties we care about
                // console.log("Success reducer - incoming myRoles:", action.payload.myRoles);
                // console.log("Success reducer - incoming activeRole:", action.payload.activeRole); 

                // Keep myRoles as is since it's already an array
                const myRoles = action.payload.myRoles || [];

                state.data = {
                    ...action.payload,
                    myRoles,
                    isAuthenticated: true,
                    loading: false
                } as UserStateV2;

                state.status = 'finished';
                // Log the final state properties we care about
               
            },
            prepare: (userProfile: UserStateV2): { payload: UserStateV2 } => {
                // Log specific properties we care about
                

                const converted = convertFirestoreTypes(userProfile);
                return {
                    payload: converted
                };
            }
        },
        setAuthenticated: (state, action: PayloadAction<boolean>) => {
            if (state.data) {
                state.data.isAuthenticated = action.payload;
            }
        },
        setDetailsFromFillDetailsForm: {
            reducer: (state, action: PayloadAction<Partial<UserStateV2>>) => {
                if (state.data) {
                   
                    state.data = { ...action.payload, ...state.data };
                }
            },
            prepare: (details: Partial<UserStateV2>): { payload: Partial<UserStateV2> } => {
                
                const converted = convertFirestoreTypes(details);
                return { payload: converted };
            }
        },
        setPhoneNumber: (state, action: PayloadAction<string>) => {
            if (state.data) {
                state.data.phone_number = action.payload;
            } else {
                state.data = {
                    phone_number: action.payload
                } as UserStateV2;
            }
        },
        setOtherDetails: (state, action: PayloadAction<{ uid: string }>) => {
            if (!state.data) {
                state.data = { uid: action.payload.uid } as UserStateV2;
            } else {
                state.data.uid = action.payload.uid;
            }
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            if (state.data) {
                state.data.loading = action.payload;
            }
        },
        setError: (state, action: PayloadAction<string | null>) => {
            if (state.data) {
                state.data.error = action.payload;
            }
        },
        setActiveRole: (state, action: PayloadAction<ServiceProviderRole | null>) => {
            if (state.data) {
                // Clear administered payment profiles when changing roles
                state.data.administeredPaymentProfiles = null;
                state.data.activeRole = action.payload;
            }
        },
        clearActiveRole: (state) => {
            if (state.data) {
                state.data.activeRole = null;
            }
        },
        clearAdministeredPaymentProfiles: (state) => {
            if (state.data) {
                state.data.administeredPaymentProfiles = null;
            }
        },
        updateAdministeredPaymentProfiles: (state, action: PayloadAction<any>) => {
            if (state.data) {
                state.data.administeredPaymentProfiles = action.payload;
            }
        }
    },
    extraReducers: (builder: ActionReducerMapBuilder<State>) => {
        builder
            .addCase(checkUserInFirestore.pending, (state: State) => {
                
                state.status = 'loading';
                if (state.data) {
                    state.data.loading = true;
                }
            })
            .addCase(checkUserInFirestore.fulfilled, (state: State, action: PayloadAction<UserStateV2 | null>) => {
                try {
               
                if (action.payload) {

                    console.log("came here -1.8", action.payload)
                  
                    // Preserve existing roles if they exist and new payload doesn't have them
                    const existingRoles = state.data?.myRoles || [];
                    const existingActiveRole = state.data?.activeRole;

                    const newRoles = Array.isArray(action.payload.myRoles) ? action.payload.myRoles :
                        (action.payload.myRoles ? [action.payload.myRoles] : existingRoles);

                    state.data = {
                        ...state.data, // Preserve existing state
                        ...action.payload,

                        myRoles: newRoles.length > 0 ? newRoles : existingRoles,
                        activeRole: action.payload.activeRole || existingActiveRole,

                        isAuthenticated: true,
                        loading: false
                    };
                    state.status = 'finished';

                    // Log specific properties we care about
                  
                } else {

                    state.data = {
                        ...state.data,
                        isAuthenticated: true,
                        loading: false
                    } as UserStateV2;

                    state.status = 'finished';

                }
            } catch (error) {
                console.error("Error in checkUserInFirestore: 1.99", error);
               
            }
            })
            .addCase(checkUserInFirestore.rejected, (state: State, action) => {
                console.error("checkUserInFirestore rejected:", action.error);
                state.status = 'error';
                state.error = action.error.message || 'Failed to fetch user data';
            })
            .addCase(saveUserData.pending, (state) => {
                if (state.data) {
                    state.data.loading = true;
                }
            })
            .addCase(saveUserData.fulfilled, (state, action) => {
                if (state.data) {
                    state.data = {
                        ...state.data,
                        ...action.payload,
                        loading: false,
                        isAuthenticated: true
                    };
                }
            })
            .addCase(saveUserData.rejected, (state: State, action) => {
                state.error = action.payload as string;
                if (state.data) {
                    state.data.loading = false;
                }
            })
            .addCase(uploadProfilePicture.fulfilled, (state: State, action: PayloadAction<string>) => {
                if (state.data) {
                    state.data.photo_url = action.payload;
                    state.data.loading = false;
                }
            })
            .addCase(persistActiveRole.fulfilled, (state, action) => {
                if (state.data) {
                    const payload = action.payload as { role: ServiceProviderRole | null, administeredPaymentProfiles: PaymentProfile[] | null, chequeredEnabled?: boolean };
                    state.data.activeRole = payload.role;
                    state.data.administeredPaymentProfiles = payload.administeredPaymentProfiles;
                    
                    // Update chequeredEnabled if provided
                    if (payload.chequeredEnabled !== undefined) {
                        state.data.chequeredEnabled = payload.chequeredEnabled;
                    }

                    // If switching back to owner (clearing active role), ensure we're using the original payment profiles
                    if (!payload.role) {
                        state.data.administeredPaymentProfiles = null;
                    }
                }
            })
            .addCase(fetchChequeredEnabled.fulfilled, (state, action: PayloadAction<boolean>) => {
                if (state.data) {
                    state.data.chequeredEnabled = action.payload;
                }
            })
            .addCase(fetchAdministeredPaymentProfiles.fulfilled, (state, action: PayloadAction<any>) => {
                if (state.data) {
                    state.data.administeredPaymentProfiles = action.payload;
                    // Log only the relevant properties
                    // ("Updated state with new profiles - administeredPaymentProfiles:", state.data.administeredPaymentProfiles);
                }
            })
            .addCase(fetchAdministeredPaymentProfiles.rejected, (state) => {
                if (state.data) {
                    state.data.administeredPaymentProfiles = null;
                }
            });
    }
});

export const saveUserData = createAsyncThunk(
    'user/saveUserData',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { userSliceV2: State };
            const userData = state.userSliceV2.data;
            if (!userData) throw new Error('No user data found');

            // Don't save administeredPaymentProfiles to Firestore as it's managed separately
            const { administeredPaymentProfiles: _, ...filteredState } = userData;

            const userRef = doc(db, `ServiceProviders`, filteredState.uid);

            // Convert ISO date strings back to Firestore Timestamps for storage
            const dataToSave = {
                ...filteredState,
                businessProfile: {
                    ...filteredState.businessProfile,
                    businessName: filteredState.businessProfile?.businessName || ''
                },
                myRoles: Array.isArray(filteredState.myRoles) ? filteredState.myRoles : [],
                addDummySubscription: false,
                created_time: Timestamp.fromDate(new Date(filteredState.createdAt)),
                edited_time: Timestamp.fromDate(new Date())
            };

            await setDoc(userRef, dataToSave);

            if (filteredState.addDummySubscription) {
                setTimeout(() => updateDoc(userRef, { addDummySubscription: true }), 2000);
            }

            return convertFirestoreTypes(dataToSave);
        } catch (error: any) {
            console.log("error -1", error)
            return rejectWithValue(error.message);
        }
    }
);

export const {
    setOtherDetails,
    setPhoneNumber,
    setAuthenticated,
    setDetailsFromFillDetailsForm,
    setLoading,
    setError,
    setActiveRole,
    clearActiveRole,
    clearAdministeredPaymentProfiles
} = UserSliceV2.actions;

export const genericActions: GenericActions<DocumentData> = {
    loading: UserSliceV2.actions.loading,
    success: UserSliceV2.actions.success as unknown as (data: DocumentData) => void,
    error: UserSliceV2.actions.error,
    reset: UserSliceV2.actions.reset 
};

export const actions = {
    setOtherDetails,
    setPhoneNumber,
    setAuthenticated,
    setDetailsFromFillDetailsForm,
    setLoading,
    setError,
    checkUserInFirestore,
    uploadProfilePicture,
    saveUserData,
    success: UserSliceV2.actions.success as unknown as (data: UserStateV2) => PayloadAction<UserStateV2>
};

export default UserSliceV2.reducer;

// Remove loading and isAuthenticated from the selector
export const selectUserSliceV2Data = (state: RootState) => {
    
    return state.userSliceV2.data;
};